import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Table, 
  Badge, 
  Spinner, 
  Alert,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';
import { generateContractHTML } from '../../utils/contractGenerator';

const ProviderBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractHTML, setContractHTML] = useState('');
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [currentBookingStatus, setCurrentBookingStatus] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch voucher bookings
      const response = await axiosInstance.get('/api/accounts/voucher-bookings/');
      
      // Filter bookings to only show those for the provider's properties
      // This assumes the API returns all bookings, we need to filter by property owner
      setBookings(response.data);
      
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <Badge color="secondary">Unknown</Badge>;
    
    // Normalize status to lowercase for comparison
    const normalizedStatus = status.toLowerCase();
    
    const statusConfig = {
      'pending': { color: 'warning', text: 'Pending BMO Approval' },
      'pending bmo approval': { color: 'warning', text: 'Pending BMO Approval' },
      'pending provider approval': { color: 'info', text: 'Pending Provider Approval' },
      'pending student contract acceptance': { color: 'primary', text: 'Awaiting Student Signature' },
      'pending provider contract acceptance': { color: 'warning', text: 'Awaiting Your Signature' },
      'pending bmo contract acceptance': { color: 'info', text: 'Awaiting BMO Approval' },
      'approved': { color: 'success', text: 'Approved by BMO' },
      'approved by bmo': { color: 'success', text: 'Approved by BMO' },
      'confirmed': { color: 'success', text: 'Confirmed' },
      'rejected': { color: 'danger', text: 'Rejected by BMO' },
      'rejected by bmo': { color: 'danger', text: 'Rejected by BMO' },
      'cancelled': { color: 'secondary', text: 'Cancelled by Student' },
      'cancelled by student': { color: 'secondary', text: 'Cancelled by Student' },
      'completed': { color: 'info', text: 'Booking Completed' },
      'booking completed': { color: 'info', text: 'Booking Completed' }
    };
    
    const config = statusConfig[normalizedStatus] || { color: 'secondary', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    toggleModal();
  };

  const viewStudentDetails = (booking) => {
    navigate('/provider-dashboard/student-details', { state: { booking } });
  };

  const handleAcceptBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to approve this booking for ${booking.student_name}?`)) {
      return;
    }

    try {
      await axiosInstance.patch(`/api/accounts/voucher-bookings/${booking.id}/`, {
        booking_status: 'pending student contract acceptance'
      });

      alert('Booking approved successfully! Student will be notified via email.');
      fetchBookings(); // Refresh the list
    } catch (err) {
      console.error('Error approving booking:', err);
      alert('Failed to approve booking. Please try again.');
    }
  };

  const handleRejectBooking = async (booking) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    
    if (reason === null) {
      return; // User cancelled
    }

    if (!reason.trim()) {
      alert('Please provide a reason for rejection.');
      return;
    }

    try {
      await axiosInstance.patch(`/api/accounts/voucher-bookings/${booking.id}/`, {
        booking_status: 'rejected',
        rejection_reason: reason
      });

      alert('Booking rejected successfully.');
      fetchBookings(); // Refresh the list
    } catch (err) {
      console.error('Error rejecting booking:', err);
      alert('Failed to reject booking. Please try again.');
    }
  };

  const handleViewContract = async (booking) => {
    try {
      setContractLoading(true);
      
      // Fetch complete contract details from backend
      const response = await axiosInstance.get(
        `/api/accounts/voucher-bookings/${booking.id}/contract-details/`
      );
      const contractData = response.data;
      
      // Generate HTML contract
      const html = generateContractHTML(contractData);
      
      // Show in modal
      setContractHTML(html);
      setCurrentBookingId(booking.id);
      setCurrentBookingStatus(booking.booking_status);
      setShowContractModal(true);
    } catch (err) {
      console.error("Error loading contract:", err);
      alert(`Failed to load contract: ${err.response?.data?.detail || err.message}`);
    } finally {
      setContractLoading(false);
    }
  };

  const handleAcceptContract = async () => {
    try {
      setContractLoading(true);
      
      const response = await axiosInstance.post(
        `/api/accounts/voucher-bookings/${currentBookingId}/provider-accept-contract/`
      );
      
      if (response.status === 200 || response.status === 201) {
        alert('Thank you for accepting the lease agreement!\n\nThe BMO will now review the contract.');
        setShowContractModal(false);
        // Refresh bookings list
        fetchBookings();
      }
    } catch (err) {
      console.error("Error accepting contract:", err);
      alert(`Failed to accept contract: ${err.response?.data?.error || err.message}`);
    } finally {
      setContractLoading(false);
    }
  };

  const handleDeclineContract = () => {
    if (window.confirm('Are you sure you want to decline this lease agreement?')) {
      setShowContractModal(false);
      alert('You have declined the lease agreement.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle tag="h4">Accommodation Bookings</CardTitle>
                <Button color="primary" size="sm" onClick={fetchBookings}>
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </Button>
              </div>

              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              {bookings.length === 0 ? (
                <Alert color="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No bookings found for your properties yet.
                </Alert>
              ) : (
                <div className="table-responsive" style={{ paddingBottom: '200px' }}>
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Student</th>
                        <th>Property</th>
                        <th>Check-in</th>
                        <th>Check-out</th>
                        <th>Voucher</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td>#{booking.id}</td>
                          <td>
                            <div>
                              <div>{booking.student_name || 'N/A'}</div>
                              <small className="text-muted">{booking.student_email}</small>
                            </div>
                          </td>
                          <td>{booking.property_name}</td>
                          <td>{formatDate(booking.check_in_date)}</td>
                          <td>{formatDate(booking.check_out_date)}</td>
                          <td>
                            <code className="text-primary">{booking.voucher_code}</code>
                          </td>
                          <td>{getStatusBadge(booking.booking_status)}</td>
                          <td>
                            <UncontrolledDropdown>
                              <DropdownToggle size="sm" color="outline-primary">
                                ⋯
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={() => viewBookingDetails(booking)}>
                                  <i className="fas fa-file-alt me-2"></i>
                                  Booking Details
                                </DropdownItem>
                                <DropdownItem onClick={() => viewStudentDetails(booking)}>
                                  <i className="fas fa-user me-2"></i>
                                  Student Details
                                </DropdownItem>
                                {['pending provider contract acceptance', 'pending bmo contract acceptance', 'confirmed'].includes(booking.booking_status?.toLowerCase()) && (
                                  <>
                                    <DropdownItem divider />
                                    <DropdownItem 
                                      onClick={() => handleViewContract(booking)}
                                      disabled={contractLoading}
                                    >
                                      <i className="bi bi-file-earmark-text me-2"></i>
                                      {contractLoading ? 'Loading...' : 'View Contract'}
                                    </DropdownItem>
                                  </>
                                )}
                                {['pending provider approval', 'approved', 'approved by bmo'].includes(booking.booking_status?.toLowerCase()) && (
                                  <>
                                    <DropdownItem divider />
                                    <DropdownItem 
                                      onClick={() => handleAcceptBooking(booking)}
                                      className="text-success"
                                    >
                                      <i className="fas fa-check me-2"></i>
                                      Accept Booking
                                    </DropdownItem>
                                  </>
                                )}
                                {['pending', 'pending bmo approval', 'pending provider approval', 'approved'].includes(booking.booking_status?.toLowerCase()) && (
                                  <>
                                    {['pending provider approval'].includes(booking.booking_status?.toLowerCase()) && <DropdownItem divider />}
                                    {['approved'].includes(booking.booking_status?.toLowerCase()) && <DropdownItem divider />}
                                    {['pending', 'pending bmo approval'].includes(booking.booking_status?.toLowerCase()) && <DropdownItem divider />}
                                    <DropdownItem 
                                      onClick={() => handleRejectBooking(booking)}
                                      className="text-danger"
                                    >
                                      <i className="fas fa-times me-2"></i>
                                      Reject Booking
                                    </DropdownItem>
                                  </>
                                )}
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Booking Details Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          Booking Details - #{selectedBooking?.id}
        </ModalHeader>
        <ModalBody>
          {selectedBooking && (
            <div>
              <Row className="mb-3">
                <Col md="6">
                  <h6 className="text-primary">Student Information</h6>
                  <p className="mb-1">
                    <strong>Name:</strong> {selectedBooking.student_name || 'N/A'}
                  </p>
                  <p className="mb-1">
                    <strong>Email:</strong> {selectedBooking.student_email}
                  </p>
                  <p className="mb-1">
                    <strong>Phone:</strong> {selectedBooking.student_phone || 'N/A'}
                  </p>
                </Col>
                <Col md="6">
                  <h6 className="text-primary">Property Information</h6>
                  <p className="mb-1">
                    <strong>Property:</strong> {selectedBooking.property_name}
                  </p>
                  <p className="mb-1">
                    <strong>Check-in:</strong> {formatDate(selectedBooking.check_in_date)}
                  </p>
                  <p className="mb-1">
                    <strong>Check-out:</strong> {formatDate(selectedBooking.check_out_date)}
                  </p>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md="6">
                  <h6 className="text-primary">Voucher Information</h6>
                  <p className="mb-1">
                    <strong>Code:</strong> <code>{selectedBooking.voucher_code}</code>
                  </p>
                  <p className="mb-1">
                    <strong>Amount:</strong> {formatCurrency(selectedBooking.voucher_amount || 0)}
                  </p>
                </Col>
                <Col md="6">
                  <h6 className="text-primary">Booking Status</h6>
                  <p className="mb-1">
                    <strong>Status:</strong> {getStatusBadge(selectedBooking.booking_status)}
                  </p>
                  <p className="mb-1">
                    <strong>Created:</strong> {formatDate(selectedBooking.created_at)}
                  </p>
                </Col>
              </Row>

              {selectedBooking.room_number && (
                <Row className="mb-3">
                  <Col>
                    <h6 className="text-primary">Assigned Accommodation</h6>
                    <Alert color="info" className="mb-0">
                      <p className="mb-1">
                        <strong>Room:</strong> {selectedBooking.room_number}
                      </p>
                      {selectedBooking.block_number && (
                        <p className="mb-1">
                          <strong>Block:</strong> {selectedBooking.block_number}
                        </p>
                      )}
                      {selectedBooking.floor_number && (
                        <p className="mb-1">
                          <strong>Floor:</strong> {selectedBooking.floor_number}
                        </p>
                      )}
                      {selectedBooking.bed_number && (
                        <p className="mb-0">
                          <strong>Bed:</strong> {selectedBooking.bed_number}
                        </p>
                      )}
                    </Alert>
                  </Col>
                </Row>
              )}

              {selectedBooking.special_requests && (
                <Row>
                  <Col>
                    <h6 className="text-primary">Special Requests</h6>
                    <Alert color="light" className="mb-0">
                      {selectedBooking.special_requests}
                    </Alert>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Contract Viewing Modal */}
      <Modal isOpen={showContractModal} toggle={() => setShowContractModal(false)} size="xl" style={{maxWidth: '95%'}}>
        <ModalHeader toggle={() => setShowContractModal(false)}>
          Lease Agreement
        </ModalHeader>
        <ModalBody style={{maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', padding: 0}}>
          <div dangerouslySetInnerHTML={{ __html: contractHTML }} />
        </ModalBody>
        <ModalFooter style={{backgroundColor: '#f8f9fa', borderTop: '3px solid #36669f'}}>
          <div style={{width: '100%'}}>
            {currentBookingStatus === 'pending provider contract acceptance' ? (
              <>
                {/* Active state - can accept */}
                <div style={{marginBottom: '15px'}}>
                  <label style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    cursor: 'pointer'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      style={{width: '20px', height: '20px'}}
                    />
                    <strong>
                      I have read and agree to the terms and conditions of this Student Lease Agreement.
                    </strong>
                  </label>
                </div>
                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <Button 
                    color="secondary" 
                    onClick={handleDeclineContract} 
                    disabled={contractLoading}
                  >
                    Decline
                  </Button>
                  <Button 
                    color="primary" 
                    onClick={handleAcceptContract} 
                    disabled={!agreeToTerms || contractLoading}
                  >
                    {contractLoading ? 'Processing...' : 'Accept Agreement'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Locked state - already accepted or other status */}
                <div style={{marginBottom: '15px'}}>
                  <label style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    opacity: 0.6, 
                    cursor: 'not-allowed'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={true}
                      disabled={true}
                      style={{width: '20px', height: '20px'}}
                    />
                    <strong>
                      I have read and agree to the terms and conditions of this Student Lease Agreement.
                    </strong>
                  </label>
                  <div style={{
                    marginTop: '10px', 
                    padding: '10px', 
                    backgroundColor: '#d1ecf1', 
                    borderLeft: '4px solid #0c5460', 
                    borderRadius: '4px'
                  }}>
                    <i className="bi bi-check-circle-fill me-2" style={{color: '#0c5460'}}></i>
                    <strong style={{color: '#0c5460'}}>Contract Accepted</strong>
                    <p style={{
                      margin: '5px 0 0 0', 
                      color: '#0c5460', 
                      fontSize: '14px'
                    }}>
                      {currentBookingStatus === 'pending bmo contract acceptance' 
                        ? 'You have already accepted this contract. Awaiting BMO approval.'
                        : 'This contract has been finalized.'}
                    </p>
                  </div>
                </div>
                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <Button 
                    color="secondary" 
                    onClick={() => setShowContractModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ProviderBookings;
